import { useEffect, useState } from "react";
import Header from "@/components/Header";
import TeamPanel, { roleLabel } from "@/components/TeamPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Stat = ({ label, value, testid }) => (
  <Card className="bg-zinc-950 border-amber-500/20" data-testid={testid}>
    <CardContent className="p-5">
      <div className="text-[11px] uppercase font-display tracking-widest text-amber-400">{label}</div>
      <div className="mt-1 font-mono-stat text-3xl">{value}</div>
    </CardContent>
  </Card>
);

export default function TeamDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [ledger, setLedger] = useState([]);
  const load = () => {
    api.get("/team/stats").then((r) => setStats(r.data));
    api.get("/team/ledger").then((r) => setLedger(r.data.items));
  };
  useEffect(load, []);
  if (!user) return null;
  const child = roleLabel(stats?.child_role || "");

  return (
    <div className="min-h-screen bg-[#09090D] text-zinc-100">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
        <div className="text-xs uppercase font-display tracking-widest text-amber-400">{roleLabel(user.role)} Panel</div>
        <h1 className="font-display text-4xl font-black uppercase gold-text mt-1">{user.display_name}</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
          <Stat label="My Balance" value={(stats?.my_credits ?? 0).toFixed(2)} testid="stat-my-balance" />
          <Stat label={`${child}s`} value={stats?.direct_total ?? "—"} testid="stat-direct-total" />
          <Stat label={`Active ${child}s`} value={stats?.direct_active ?? "—"} testid="stat-direct-active" />
          <Stat label={`${child} Balance`} value={(stats?.direct_balance ?? 0).toFixed(2)} testid="stat-direct-balance" />
        </div>
        <Tabs defaultValue="team">
          <TabsList className="bg-zinc-950 border border-amber-500/20">
            <TabsTrigger value="team" data-testid="tab-team" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">{child}s</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-team-ledger" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Statement</TabsTrigger>
          </TabsList>
          <TabsContent value="team" className="mt-6"><TeamPanel onChanged={load} /></TabsContent>
          <TabsContent value="ledger" className="mt-6">
            <Card className="bg-zinc-950 border-amber-500/20">
              <CardContent className="p-0">
                {ledger.length === 0 && <div className="p-6 text-zinc-500 text-sm">No transactions.</div>}
                {ledger.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-sm">
                    <div className="col-span-3 text-xs text-zinc-400">{new Date(l.at).toLocaleString()}</div>
                    <div className="col-span-5">{l.reason} {l.client_id !== user.id && <span className="text-zinc-500 text-xs">· downline</span>}</div>
                    <div className={`col-span-2 font-mono-stat ${l.delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>{l.delta >= 0 ? "+" : ""}{l.delta.toFixed(2)}</div>
                    <div className="col-span-2 font-mono-stat text-amber-300">{l.new_balance.toFixed(2)}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
